import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { presentError } from "../../lib/errors.js";
import { queryKeys } from "../../lib/query.js";
import type { Books, CustomerSiteRow, InventoryRow, OrgSiteBooks, ProjectRow } from "./hub-operation-types.js";

export const SITE_STATUSES = [
	["active", "Active"],
	["inactive", "Inactive"],
] as const;

/** Legacy work rows may still carry status `new`; it is not offered for new work. */
export const LEGACY_WORK_STATUS = "new";

export const DEFAULT_WORK_STATUS = "progress";

export const JOB_STATUSES = [
	["progress", "In progress"],
	["done", "Done"],
] as const;

export const WORK_TYPE_OPTIONS = [
	["site-visit", "Site Visit"],
	["maintenance", "Maintenance"],
	["installation", "Installation"],
	["inspection", "Inspection"],
	["warranty", "Warranty"],
] as const;

export type WorkTypeSlug = (typeof WORK_TYPE_OPTIONS)[number][0];

const WORK_TYPE_LABEL = new Map<string, string>(WORK_TYPE_OPTIONS);

export function workStatusLabel(status: string): string {
	if (status === LEGACY_WORK_STATUS) return "New";
	return JOB_STATUSES.find(([value]) => value === status)?.[1] ?? status;
}

export function normalizeWorkTypes(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	const seen = new Set<string>();
	const rows: string[] = [];
	for (const entry of value) {
		const slug = String(entry ?? "").trim();
		if (!slug || seen.has(slug)) continue;
		seen.add(slug);
		rows.push(slug);
	}
	return rows;
}

export function workTypesOf(row: { workTypes?: unknown }): string[] {
	return normalizeWorkTypes(row.workTypes);
}

export function workTypeLabel(slug: string): string {
	const trimmed = slug.trim();
	if (!trimmed) return "";
	return WORK_TYPE_LABEL.get(trimmed) ?? trimmed;
}

export function workTypesFromForm(fields: FormData): string[] {
	return normalizeWorkTypes(fields.getAll("workType").map(String));
}

export function assigneeNamesOf(value: string | undefined): string[] {
	const seen = new Set<string>();
	const names: string[] = [];
	for (const item of String(value ?? "").split(",")) {
		const name = item.trim();
		if (!name) continue;
		const key = name.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		names.push(name);
	}
	return names;
}

export function assigneeFieldFromNames(names: string[]): string {
	return names.join(", ");
}

export function personDisplayName(name: string): string {
	return name.trim().split(/\s+/).map((part) => {
		if (!part || part.includes("@") || part.startsWith("(")) return part;
		return part.charAt(0).toUpperCase() + part.slice(1);
	}).join(" ");
}

export function assigneeSummaryLabel(picked: string[]): string {
	if (picked.length === 0) return "Unassigned";
	if (picked.length === 1) return personDisplayName(picked[0]!);
	return `${personDisplayName(picked[0]!)} +${picked.length - 1}`;
}

export function resolveAssigneePick(raw: string, options: string[]): string {
	const key = raw.trim().toLowerCase();
	if (!key) return "";
	return options.find((name) => name.trim().toLowerCase() === key) ?? "";
}

export function resolveAssigneePicks(raw: string, options: string[]): string[] {
	const seen = new Set<string>();
	const picks: string[] = [];
	for (const part of assigneeNamesOf(raw)) {
		const resolved = resolveAssigneePick(part, options);
		if (!resolved) continue;
		const key = resolved.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		picks.push(resolved);
	}
	return picks;
}

export function textOf(fields: FormData, name: string): string {
	return String(fields.get(name) ?? "").trim();
}

export function customersOf(books: OrgSiteBooks) {
	return books.organisations.filter((row) => row.kind === "customer");
}

export function customerLabel(books: OrgSiteBooks, customerId: string): string {
	if (!customerId) return "";
	const row = books.organisations.find((item) => String(item.id ?? "") === customerId);
	return row?.name ?? "";
}

export function siteRow(books: OrgSiteBooks, siteId: string): CustomerSiteRow | undefined {
	return books.customerSites.find((row) => String(row.id ?? "") === siteId);
}

export function projectRow(books: Books, projectId: string): ProjectRow | undefined {
	return books.projects.find((row) => String(row.id ?? "") === projectId);
}

export function sitesForCustomer(books: OrgSiteBooks, customerId: string): CustomerSiteRow[] {
	if (!customerId) return [];
	return books.customerSites.filter((row) => row.customerId === customerId);
}

export function vendorsOf(books: OrgSiteBooks) {
	return books.organisations.filter((row) => row.kind === "vendor");
}

export function vendorLabel(books: OrgSiteBooks, vendorId: string): string {
	if (!vendorId) return "";
	const row = books.organisations.find((item) => String(item.id ?? "") === vendorId);
	return row?.name ?? "";
}

export function inventoryRow(books: Books, itemId: string): InventoryRow | undefined {
	return (books.inventory ?? []).find((row) => String(row.id ?? "") === itemId);
}

export function nextInventoryAstId(rows: Array<{ astId?: string }>): string {
	let max = 0;
	for (const row of rows) {
		const n = Number(String(row.astId ?? "").replace(/^AST-/i, ""));
		if (Number.isFinite(n)) max = Math.max(max, n);
	}
	return `AST-${max + 1}`;
}

export function formatMyr(amount: string): string {
	const n = Number(amount);
	if (!amount.trim() || !Number.isFinite(n)) return "—";
	return new Intl.NumberFormat("en-MY", { style: "currency", currency: "MYR" }).format(n);
}

export const INVENTORY_STATUSES = [
	["active", "Active"],
	["inactive", "Inactive"],
	["loose", "Loose"],
] as const;

export const INVENTORY_CUSTODY_TAGS = [
	["internal", "On Hands"],
	["external", "With Client"],
] as const;

export function useHubSave(save: (fields: FormData) => Promise<void>) {
	const client = useQueryClient();
	const [open, setOpen] = useState(false);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState("");
	return {
		open,
		setOpen,
		pending,
		error,
		save: async (fields: FormData) => {
			setPending(true);
			setError("");
			try {
				await save(fields);
				await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
				setOpen(false);
			} catch (cause) {
				setError(presentError(cause).title);
			} finally {
				setPending(false);
			}
		},
	};
}
